import { useState, useEffect } from 'react';
import { Button, Select, Stack, Text, createListCollection } from '@chakra-ui/react';
import { coordinateService } from '../../lib/services/coordinateService';
import { useMap } from '../map/map-context';
import { fromLonLat } from 'ol/proj';
import { StorageManager } from '../../lib/utils/storage-manager';

const storageManager = StorageManager.getInstance();

export default function LLDSearch({ onClose }: { onClose: () => void }) {
  const [section, setSection] = useState('');
  const [township, setTownship] = useState('');
  const [range, setRange] = useState('');
  const [meridian, setMeridian] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { map } = useMap();

  // Load saved values on component mount
  useEffect(() => {
    const savedState = storageManager.loadLLDState();
    setSection(savedState.section);
    setTownship(savedState.township);
    setRange(savedState.range);
    setMeridian(savedState.meridian);
  }, []);

  // Save values whenever they change
  useEffect(() => {
    if (section || township || range || meridian) {
      storageManager.saveLLDState(section, township, range, meridian);
    }
  }, [section, township, range, meridian]);

  const sectionCollection = createListCollection({
    items: Array.from({ length: 36 }, (_, i) => ({
      label: (i + 1).toString(),
      value: (i + 1).toString(),
    })),
  });

  const townshipCollection = createListCollection({
    items: [
      ...Array.from({ length: 65 }, (_, i) => ({
        label: (i + 1).toString(),
        value: (i + 1).toString(),
      })),
      ...['19A', '27A', '42A', '43A', '45A', '46A', '47A'].map((township) => ({
        label: township,
        value: township,
      })),
    ].sort((a, b) => {
      // Extract numeric part and letter part
      const aNum = parseInt(a.label);
      const bNum = parseInt(b.label);
      
      // If both are pure numbers, sort numerically
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      
      // If one has letters and one doesn't, put the pure number first
      if (!isNaN(aNum) && isNaN(bNum)) {
        return -1;
      }
      if (isNaN(aNum) && !isNaN(bNum)) {
        return 1;
      }
      
      // If both have letters, sort numerically first, then alphabetically
      const aNumPart = parseInt(a.label);
      const bNumPart = parseInt(b.label);
      if (aNumPart !== bNumPart) {
        return aNumPart - bNumPart;
      }
      
      // If numeric parts are equal, sort by the letter part
      return a.label.localeCompare(b.label);
    }),
  });

  const rangeCollection = createListCollection({
    items: [
      ...Array.from({ length: 35 }, (_, i) => ({
        label: (i + 1).toString(),
        value: (i + 1).toString(),
      })),
      ...['13A', '21A', '24A'].map((township) => ({
        label: township,
        value: township,
      })),
    ].sort((a, b) => {
      // Extract numeric part and letter part
      const aNum = parseInt(a.label);
      const bNum = parseInt(b.label);
      
      // If both are pure numbers, sort numerically
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      
      // If one has letters and one doesn't, put the pure number first
      if (!isNaN(aNum) && isNaN(bNum)) {
        return -1;
      }
      if (isNaN(aNum) && !isNaN(bNum)) {
        return 1;
      }
      
      // If both have letters, sort numerically first, then alphabetically
      const aNumPart = parseInt(a.label);
      const bNumPart = parseInt(b.label);
      if (aNumPart !== bNumPart) {
        return aNumPart - bNumPart;
      }
      
      // If numeric parts are equal, sort by the letter part
      return a.label.localeCompare(b.label);
    }),
  });

  const meridianCollection = createListCollection({
    items: [
      { label: 'W1', value: '1' },
      { label: 'W2', value: '2' },
      { label: 'W3', value: '3' },
      { label: 'W4', value: '4' },
    ],
  });

  const handleSearch = async () => {
    if (!section || !township || !range || !meridian) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const coordinates = await coordinateService.getCenterOfSection(
        toLeadingZeroString(parseInt(section)),
        toLeadingZeroString(parseInt(township)),
        toLeadingZeroString(parseInt(range)),
        meridian,
      );

      if (map) {
        const view = map.getView();
        view.animate({
          center: fromLonLat([coordinates.longitude, coordinates.latitude]),
          zoom: 14,
          duration: 1000,
        });
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to find section');
    } finally {
      setLoading(false);
    }
  };

  const toLeadingZeroString = (val: number): string => {
    if (val < 10) {
      return '0' + val;
    } else {
      return val.toString();
    }
  };

  return (
    <Stack gap='4'>
      <Stack gap='3'>
        <Select.Root
          collection={sectionCollection}
          value={[section]}
          onValueChange={(e) => setSection(e.value[0])}
        >
          <Select.HiddenSelect />
          <Select.Label>Section</Select.Label>
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder="Select section" />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Content>
            {sectionCollection.items.map((item) => (
              <Select.Item key={item.value} item={item}>
                {item.label}
              </Select.Item>
            ))}
          </Select.Content>

        </Select.Root>

        <Select.Root
          collection={townshipCollection}
          value={[township]}
          onValueChange={(e) => setTownship(e.value[0])}
        >
          <Select.HiddenSelect />
          <Select.Label>Township</Select.Label>
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder="Select township" />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Content>
            {townshipCollection.items.map((item) => (
              <Select.Item key={item.value} item={item}>
                {item.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        <Select.Root
          collection={rangeCollection}
          value={[range]}
          onValueChange={(e) => setRange(e.value[0])}
        >
          <Select.HiddenSelect />
          <Select.Label>Range</Select.Label>
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder="Select range" />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Content>
            {rangeCollection.items.map((item) => (
              <Select.Item key={item.value} item={item}>
                {item.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        <Select.Root
          collection={meridianCollection}
          value={[meridian]}
          onValueChange={(e) => setMeridian(e.value[0])}
        >
          <Select.HiddenSelect />
          <Select.Label>Meridian</Select.Label>
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder="Select meridian" />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Content>
            {meridianCollection.items.map((item) => (
              <Select.Item key={item.value} item={item}>
                {item.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Stack>

      {error && (
        <Text color='red.500' fontSize='sm'>
          {error}
        </Text>
      )}

      <Button
        onClick={handleSearch}
        loading={loading}
        disabled={!section || !township || !range || !meridian}
      >
        Search LLD
      </Button>
    </Stack>
  );
}
